import React from 'react';
import './App.css';
import mo360Logo from './assets/MO360ECMLogo.png';
import spectreIcon from './assets/spectre_icon.ico';
import Header from './components/Header/Header';
import SessionExpiredDialog from './components/SessionExpiredDialog/SessionExpiredDialog';
import LoginFailureDialog from './components/LoginFailureDialog/LoginFailureDialog';
import {
    AuthProvider,
    useAuth,
    canAccessEcm,
    canAccessSpectre,
    isPathAllowed,
    resolveLandingPath
} from './context/AuthContext';

/**
 * Main content component that uses auth context
 */
function AppContent() {
    const { session, isLoading, isAuthenticated, sessionExpired, loginFailed, handleSessionExpiredLogin, handleLoginFailureRetry } = useAuth();
    const [pathname, setPathname] = React.useState(window.location.pathname);

    // Keep the rendered path in sync with the address bar. `popstate` covers back/forward and the
    // explicit dispatch done after a redirect, `single-spa:routing-event` covers navigations
    // performed by the microfrontends through the patched history API.
    React.useEffect(() => {
        const handleNavigation = () => setPathname(window.location.pathname);
        window.addEventListener('popstate', handleNavigation);
        window.addEventListener('single-spa:routing-event', handleNavigation);
        return () => {
            window.removeEventListener('popstate', handleNavigation);
            window.removeEventListener('single-spa:routing-event', handleNavigation);
        };
    }, []);

    const landingPath = resolveLandingPath(session);
    const isForbiddenRoute = isAuthenticated && !isPathAllowed(pathname, session);
    // A user entitled to a single application never sees the tiles: the shell forwards them.
    const needsLandingRedirect = isAuthenticated && pathname === '/' && landingPath !== '/';

    /**
     * Rewrite the URL and let single-spa re-evaluate which microfrontend may be active.
     * `replaceState` is deliberate: the blocked URL must not stay in the history stack, otherwise
     * the back button would drop the user straight back onto it.
     */
    const redirectTo = React.useCallback((path: string) => {
        if (window.location.pathname === path) {
            return;
        }
        window.history.replaceState(null, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setPathname(path);
    }, []);

    // Route protection. Runs on every navigation and on every session refresh, so entering a
    // forbidden path in the address bar, using back/forward, or losing an entitlement mid-session
    // all end up on a path the user is allowed to see.
    React.useEffect(() => {
        if (isForbiddenRoute || needsLandingRedirect) {
            redirectTo(landingPath);
        }
    }, [isForbiddenRoute, needsLandingRedirect, landingPath, redirectTo]);

    const navigateTo = (path: string) => {
        window.history.pushState(null, '', path);
        setPathname(path);
    };

    const isHome = pathname === '/';

    // Show loading state while checking session, awaiting login redirect, or while a route
    // redirect is still pending, so neither the tiles nor a forbidden route flash up.
    if (isLoading || (!isAuthenticated && !sessionExpired && !loginFailed) || isForbiddenRoute || needsLandingRedirect) {
        return (
            <div className="App loading-container">
                <div className="loading-spinner"></div>
                <span>Loading...</span>
            </div>
        );
    }

    // Show access denied when user has no access to any application
    const hasNoAccess = isAuthenticated && !canAccessEcm(session) && !canAccessSpectre(session);
    if (hasNoAccess) {
        return (
            <>
                <Header />
                <div className="App">
                    <div className="access-denied-container">
                        <div className="access-denied-icon">🔒</div>
                        <h2 className="access-denied-title">Access Denied</h2>
                        <p className="access-denied-message">
                            You don't have access to the applications. Please request access.
                        </p>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            {isHome && (
                <div className="App">
                    <div className="tiles-container">
                        {/* Show the ECM tile for every ECM user. The ECM role only governs what
                            the user can do inside ECM, so it never gates the tile. */}
                        {canAccessEcm(session) && (
                            <div className="tile-card" onClick={() => navigateTo('/ecm')}>
                                <img src={mo360Logo} className="tile-image ecm-tile-image" alt="ECM Logo" />
                                <div className="tile-text">ECM</div>
                            </div>
                        )}
                        {/* Show Spectre tile only if user has Spectre access */}
                        {canAccessSpectre(session) && (
                            <div className="tile-card" onClick={() => navigateTo('/spectre')}>
                                <img src={spectreIcon} className="tile-image" alt="Spectre Logo" />
                                <div className="tile-text">Spectre</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <SessionExpiredDialog open={sessionExpired} onLogin={handleSessionExpiredLogin} />
            <LoginFailureDialog open={loginFailed} onRetry={handleLoginFailureRetry} />
        </>
    );
}

/**
 * Root App component wrapped with AuthProvider
 */
function App() {
    return (
        <AuthProvider>
            <AppContent />
        </AuthProvider>
    );
}

export default App;
